/**
 * Le parcours au clavier des sélecteurs bâtis sur `Command` (cmdk), motif WAI-ARIA « combobox
 * avec liste » :
 *   • `useDesigneLOptionActive` garde `aria-activedescendant` du champ et de la liste sur
 *     l'option RÉELLEMENT active : celle que la liste surligne, et celle qu'Entrée choisira ;
 *   • `ouvrirALaFleche` ouvre la liste depuis le déclencheur, à ↓ ou ↑.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QUE CE MODULE RÉPARE : un lecteur d'écran qui annonce une autre option
 * ═══════════════════════════════════════════════════════════════════════════
 * cmdk 1.1.1 range l'option désignée (`selectedItemId`) dans une tâche de son ordonnanceur
 * (`dist/index.mjs`, remis en forme) :
 *
 *     setState('value', …) → v(7, () => { selectedItemId = M()?.id })
 *     où M() = list.querySelector('[cmdk-item][aria-selected="true"]')
 *
 * Quand la valeur active change PENDANT le vidage de cet ordonnanceur
 * (`n.current.forEach(u => u())`) — à l'ouverture, et après chaque frappe de recherche —, la
 * tâche 7 est ajoutée à la `Map` en cours de parcours, et `Map.forEach` la visite AUSSITÔT :
 * elle lit le DOM avant que les options ne se re-rendent. Le champ ne désigne alors rien (à
 * l'ouverture, après une recherche, sur un résultat unique qu'aucune flèche ne fera désigner),
 * ou une option PÉRIMÉE (↓ puis une saisie : 01.12Z annoncé, 01.11Z enregistré par Entrée).
 * Une valeur initiale (`defaultValue`) n'est pas désignée non plus. Seule une flèche frappée
 * hors de ce vidage tombait juste.
 *
 * Ni le champ ni la liste ne peuvent recevoir l'attribut en prop : cmdk écrit le sien APRÈS
 * celles de l'appelant. On le resynchronise donc sur le DOM, la seule vérité commune avec
 * Entrée (qui choisit `M()`) : un `MutationObserver` sur la racine du `Command` réécrit
 * l'attribut dès qu'une option change d'état, apparaît ou disparaît, ou que cmdk le réécrit
 * périmé. Le rappel tombe en microtâche, avant que le navigateur ne peigne.
 *
 * 🔴 Le DÉFILEMENT a la même cause : cmdk le programme dans le même vidage (`v(5, ne)`, qui fait
 * défiler `M()`), donc sur le DOM d'AVANT. Après une recherche, il visait l'option active
 * d'avant — ou rien si elle a disparu — et la liste restait où elle était : l'option qu'Entrée
 * enregistre pouvait être hors de vue. Le cas est systématique sur un sélecteur rouvert sur sa
 * valeur (`defaultValue` : la liste s'ouvre défilée jusqu'à elle), puis filtré. La liste
 * (`defiler`) ramène donc dans la vue l'option active quand elle change dans la même tâche que
 * la structure (options montrées, cachées, réordonnées) : une recherche, des résultats qui arrivent.
 * Pas sur un simple changement d'`aria-selected` : le survol à la souris active une option sans
 * que cmdk ne défile, et la liste ne doit pas sauter sous le pointeur ; les flèches, elles,
 * tombent hors du vidage, et cmdk les fait défiler juste.
 *
 * Les gardiens : `components/ui/__tests__/leChampDeRechercheDesigneLOptionActive.test.tsx`, et
 * les cas « rouvert sur … » de `leCodeNafDesigneSonOptionActive` / `laFormeJuridiqueDesigneSonOptionActive`.
 */
import * as React from 'react'

const ATTRIBUT = 'aria-activedescendant'
const OPTION_ACTIVE = '[cmdk-item][aria-selected="true"]'

/** Où chercher l'option active : la liste elle-même, ou celle que le champ contrôle. */
type TrouverLaListe = (noeud: HTMLElement) => HTMLElement | null

/** La liste d'un champ cmdk : celle que nomme son `aria-controls`. */
export const listeDuChamp: TrouverLaListe = (champ) => {
    const id = champ.getAttribute('aria-controls')
    return id ? champ.ownerDocument.getElementById(id) : null
}

/** La liste cmdk elle-même. */
export const laListe: TrouverLaListe = (liste) => liste

function synchroniser(noeud: HTMLElement, option: Element | null) {
    const id = option?.id || null
    if (id === noeud.getAttribute(ATTRIBUT)) return // aussi ce qui arrête l'écho de notre écriture
    if (id) noeud.setAttribute(ATTRIBUT, id)
    else noeud.removeAttribute(ATTRIBUT)
}

/**
 * Une mutation de STRUCTURE de la liste : une option ou une section montrée, cachée ou déplacée
 * (recherche, résultats qui arrivent). Pas ce qui change DANS une option (un contenu rendu selon
 * l'option active, au survol par exemple).
 */
function changeLaStructure(liste: Element, mutation: MutationRecord) {
    const cible = mutation.target as Element
    return mutation.type === 'childList' && liste.contains(cible) && !cible.closest?.('[cmdk-item]')
}

/** Fait défiler l'option dans la vue comme cmdk : d'abord le titre de sa section si elle l'ouvre. */
function ramenerDansLaVue(option: Element) {
    if (option.parentElement?.firstElementChild === option) {
        option.closest('[cmdk-group]')?.querySelector('[cmdk-group-heading]')?.scrollIntoView({ block: 'nearest' })
    }
    option.scrollIntoView({ block: 'nearest' })
}

/**
 * Un ref (à composer avec le ref transmis) qui tient `aria-activedescendant` du nœud sur
 * l'option active de sa liste, tant que le composant est monté. Avec `defiler` (la liste, qui
 * est le conteneur qui défile), il ramène aussi dans la vue l'option qu'une recherche a activée.
 *
 * ⚠️ L'observateur vit avec le COMPOSANT (un effet), pas avec le ref : la liste de cmdk compose
 * son ref à chaque rendu (`composeRefs(d, o)`, une fonction neuve), donc React le détache puis
 * le rattache à chaque frappe. Un observateur recréé à chaque rattachement JETAIT, à son
 * `disconnect()`, les mutations de structure de la recherche : il ne voyait plus que les
 * `aria-selected`, et ne savait plus qu'une recherche avait eu lieu. Le nœud d'un champ ou
 * d'une liste cmdk, lui, ne change pas tant que le composant est monté.
 */
export function useDesigneLOptionActive<T extends HTMLElement>(
    transmis: React.ForwardedRef<T>,
    trouverLaListe: TrouverLaListe,
    { defiler = false }: { defiler?: boolean } = {},
): React.RefCallback<T> {
    const monte = React.useRef<T | null>(null)

    React.useLayoutEffect(() => {
        const noeud = monte.current
        if (!noeud) return undefined

        const optionActive = () => trouverLaListe(noeud)?.querySelector(OPTION_ACTIVE) ?? null
        let idActif = optionActive()?.id || null
        // ⚠️ La structure et l'option active ne changent pas dans le MÊME lot de mutations : dans
        // un navigateur, cmdk réordonne les options dans le gestionnaire de saisie (un 1ᵉʳ lot),
        // et React ne rend la nouvelle option active qu'à la microtâche suivante (un 2ᵈ lot).
        // « La structure a changé » vaut donc jusqu'à la fin de la tâche en cours : une recherche,
        // pas le survol qui viendra après.
        let structureChangee = false
        let oubli: ReturnType<typeof setTimeout> | undefined
        const suivre = new MutationObserver((mutations) => {
            if (defiler && mutations.some((m) => changeLaStructure(noeud, m))) {
                structureChangee = true
                clearTimeout(oubli)
                oubli = setTimeout(() => (structureChangee = false), 0)
            }
            const option = optionActive()
            const id = option?.id || null
            if (option && id !== idActif && structureChangee) {
                ramenerDansLaVue(option)
                structureChangee = false
            }
            idActif = id
            synchroniser(noeud, option)
        })
        suivre.observe(noeud.closest('[cmdk-root]') ?? noeud, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['aria-selected', ATTRIBUT],
        })
        synchroniser(noeud, optionActive())
        return () => {
            clearTimeout(oubli)
            suivre.disconnect()
        }
    }, [trouverLaListe, defiler])

    return React.useCallback(
        (noeud: T | null) => {
            monte.current = noeud
            if (typeof transmis === 'function') transmis(noeud)
            else if (transmis) transmis.current = noeud
        },
        [transmis],
    )
}

/**
 * Le gestionnaire `onKeyDown` du déclencheur d'un sélecteur (le bouton `role="combobox"` qui
 * ouvre un `Popover`) : ↓ et ↑, avec ou sans Alt, ouvrent la liste, comme le demande le motif
 * combobox. Entrée et Espace l'ouvrent déjà (c'est un bouton) ; le `Popover` met ensuite le
 * focus dans le champ de recherche.
 */
export function ouvrirALaFleche(ouvrir: () => void): React.KeyboardEventHandler<HTMLElement> {
    return (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
        event.preventDefault() // sinon la page défile sous la liste qui s'ouvre
        ouvrir()
    }
}
